import React, { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { useOrdersStore } from '../../../store/ordersStore';
import { Order, OrderStatusHistoryEntry, OrderStatus } from '../../../services/ordersApi';
import { formatOrderId } from '../../../utils/formatId';
import styles from './CustomerOrderDetails.module.css';

const STATUS_STEPS: Array<{ key: OrderStatus; label: string }> = [
  { key: 'pending',   label: 'Order Placed' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'shipped',   label: 'In Transit' },
  { key: 'delivered', label: 'Delivered' },
];

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  pending:   { bg: '#FEF3C7', color: '#92400E' },
  confirmed: { bg: '#DBEAFE', color: '#1E40AF' },
  shipped:   { bg: '#E0E7FF', color: '#3730A3' },
  delivered: { bg: '#D1FAE5', color: '#065F46' },
  invoiced:  { bg: '#F3E8FF', color: '#6B21A8' },
  cancelled: { bg: '#FEE2E2', color: '#991B1B' },
};

const fmtCurrency = (n: number | string | undefined) => {
  const v = typeof n === 'string' ? parseFloat(n) : (n ?? 0);
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (iso?: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtDateTime = (iso?: string | null) => {
  if (!iso) return null;
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const CustomerOrderDetails: React.FC = () => {
  const [params] = useSearchParams();
  const id = params.get('id');

  const current = useOrdersStore((s) => s.current);
  const history = useOrdersStore((s) => s.history);
  const loading = useOrdersStore((s) => s.loading);
  const error = useOrdersStore((s) => s.error);
  const fetchOne = useOrdersStore((s) => s.fetchOne);
  const fetchHistory = useOrdersStore((s) => s.fetchHistory);

  useEffect(() => {
    if (!id) return;
    fetchOne(id).catch(() => undefined);
    fetchHistory(id).catch(() => undefined);
  }, [id, fetchOne, fetchHistory]);

  const order: Order | null = current && String(current.id) === String(id) ? current : null;

  const subtotal = useMemo(() => {
    if (!order) return 0;
    const total = Number(order.total_amount) || 0;
    const tax = Number(order.tax_total) || 0;
    return Math.max(0, total - tax);
  }, [order]);

  const itemCount = order?.items?.reduce((s, it) => s + (it.quantity || 0), 0) ?? 0;
  const statusBadgeStyle = order ? STATUS_COLORS[order.status] ?? STATUS_COLORS.pending : null;

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <Link to="/customer-orders" className={styles.backLink}>← Back to Orders</Link>
      </div>

      <AsyncBoundary
        loading={loading && !order}
        error={error}
        empty={!loading && !order}
        emptyMessage={id ? 'Order not found' : 'No order selected'}
        onRetry={id ? () => fetchOne(id) : undefined}
      >
        {order && (
          <>
            <section className={styles.heroCard}>
              <div className={styles.heroTop}>
                <div>
                  <h1 className={styles.heroOrderNumber}>
                    Order #{formatOrderId(order.id)}
                  </h1>
                  <p className={styles.heroDate}>Placed on {fmtDate(order.order_date)}</p>
                </div>
                {statusBadgeStyle && (
                  <span
                    className={styles.statusBadge}
                    style={{ background: statusBadgeStyle.bg, color: statusBadgeStyle.color }}
                  >
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </span>
                )}
              </div>

              <div className={styles.heroStats}>
                <div className={styles.heroStat}>
                  <span className={styles.heroStatLabel}>Total</span>
                  <span className={styles.heroStatValue}>{fmtCurrency(order.total_amount)}</span>
                </div>
                <div className={styles.heroStat}>
                  <span className={styles.heroStatLabel}>Items</span>
                  <span className={styles.heroStatValue}>{itemCount}</span>
                </div>
                {order.invoice && (
                  <div className={styles.heroStat}>
                    <span className={styles.heroStatLabel}>Invoice</span>
                    <span className={styles.heroStatValue}>{order.invoice.invoice_number}</span>
                  </div>
                )}
              </div>
            </section>

            <div className={styles.grid}>
              <div className={styles.left}>
                {/* Status timeline */}
                {order.status !== 'cancelled' && (
                  <section className={styles.card}>
                    <h2 className={styles.cardTitle}>Order Status</h2>
                    <Timeline status={order.status} history={history} />
                  </section>
                )}
                {order.status === 'cancelled' && (
                  <section className={`${styles.card} ${styles.cancelledCard}`}>
                    <h2 className={styles.cardTitle}>Order Cancelled</h2>
                    <p className={styles.cancelledText}>
                      {order.cancelled_at
                        ? `Cancelled on ${fmtDateTime(order.cancelled_at)}.`
                        : 'This order has been cancelled.'}
                    </p>
                  </section>
                )}

                {/* Items */}
                <section className={styles.card}>
                  <h2 className={styles.cardTitle}>Items</h2>
                  <div className={styles.itemsTableWrap}>
                    <table className={styles.itemsTable}>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th className={styles.right}>Qty</th>
                          <th className={styles.right}>Unit Price</th>
                          <th className={styles.right}>Line Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(order.items ?? []).map((it, i) => (
                          <tr key={it.id ?? `${it.sku_id}-${i}`}>
                            <td>
                              <div className={styles.itemName}>{it.product_name || `SKU #${it.sku_id}`}</div>
                              {it.sku_code && <div className={styles.itemSku}>SKU: {it.sku_code}</div>}
                            </td>
                            <td className={styles.right}>{it.quantity}</td>
                            <td className={styles.right}>{fmtCurrency(it.unit_price)}</td>
                            <td className={styles.right}>{fmtCurrency(it.line_total ?? Number(it.unit_price) * it.quantity)}</td>
                          </tr>
                        ))}
                        {(!order.items || order.items.length === 0) && (
                          <tr><td colSpan={4} className={styles.emptyRow}>No items on this order.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>

              <aside className={styles.right}>
                <section className={styles.card}>
                  <h2 className={styles.cardTitle}>Order Summary</h2>
                  <div className={styles.summaryRow}>
                    <span>Subtotal</span>
                    <span>{fmtCurrency(subtotal)}</span>
                  </div>
                  <div className={styles.summaryRow}>
                    <span>Tax</span>
                    <span>{fmtCurrency(order.tax_total)}</span>
                  </div>
                  <div className={styles.divider} />
                  <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
                    <span>Total</span>
                    <span>{fmtCurrency(order.total_amount)}</span>
                  </div>
                </section>

                {order.invoice && (
                  <section className={styles.card}>
                    <h2 className={styles.cardTitle}>Payment</h2>
                    <div className={styles.summaryRow}>
                      <span>Invoice</span>
                      <span>{order.invoice.invoice_number}</span>
                    </div>
                    <div className={styles.summaryRow}>
                      <span>Status</span>
                      <span className={styles.invoiceStatus}>
                        {order.invoice.status.charAt(0).toUpperCase() + order.invoice.status.slice(1)}
                      </span>
                    </div>
                  </section>
                )}
              </aside>
            </div>
          </>
        )}
      </AsyncBoundary>
    </div>
  );
};

interface TimelineProps {
  status: OrderStatus;
  history: OrderStatusHistoryEntry[];
}

const Timeline: React.FC<TimelineProps> = ({ status, history }) => {
  const currentIndex = STATUS_STEPS.findIndex((s) => s.key === status);
  return (
    <ol className={styles.timeline}>
      {STATUS_STEPS.map((step, i) => {
        const matching = history.find((h) => h.to_status === step.key);
        const state: 'done' | 'active' | 'pending' =
          i < currentIndex ? 'done' : i === currentIndex ? 'active' : 'pending';
        return (
          <li key={step.key} className={`${styles.timelineStep} ${styles[`timelineStep_${state}`]}`}>
            <span className={styles.timelineDot} />
            <div className={styles.timelineBody}>
              <span className={styles.timelineLabel}>{step.label}</span>
              <span className={styles.timelineMeta}>
                {matching ? fmtDateTime(matching.changed_at) : state === 'active' ? 'In progress' : 'Pending'}
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export const CustomerOrderDetailsPage: React.FC = () => <CustomerOrderDetails />;
export { CustomerOrderDetails };
export default CustomerOrderDetails;
